import React, { useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, Link } from "react-router-dom";
import { ChevronDown, Menu, Plug, X } from "lucide-react";
import { AuthProvider, useAuth } from "./lib/auth";
import { capturePageview, applyReplayPolicy } from "./lib/posthog";
import { useDenialReport } from "./lib/use-denial-report";

/**
 * Sends one $pageview per route change.
 *
 * posthog-js used to do this itself via `capture_pageview: true`, but that
 * captures the first pageview at init() — before React mounts and long before
 * Keycloak returns — so those events were attributed to an anonymous UUID
 * instead of a person. Pageviews are now captured manually and capturePageview()
 * is a no-op until identify() has run, so no event can be anonymous.
 *
 * Mounted inside BrowserRouter because useLocation requires a router context.
 * The trade is deliberate: pageviews from a genuinely logged-out visitor are no
 * longer recorded. On an app whose every route requires auth, an anonymous
 * pageview was never something anyone could act on.
 */
function PostHogPageviews(): null {
  const location = useLocation();
  const { userContext } = useAuth();
  // Depend on the SUB, not the context object. The object is rebuilt on every
  // navigation, so using it as a dependency re-ran this effect a second time per
  // route with an unchanged location -- doubling every pageview.
  const sub = userContext?.sub ?? null;
  React.useEffect(() => {
    if (!sub) return;
    // Order matters: stop the recorder BEFORE anything on the new route has a
    // chance to paint. Capturing the pageview first would be a frame of a
    // blocked route already on tape.
    applyReplayPolicy(location.pathname);
    capturePageview();
  }, [location.pathname, location.search, sub]);
  return null;
}
import { takeOAuthReturn } from "./lib/oauth-popup";
import { useEntitlement } from "./hooks/use-entitlements";
import type { EntitlementKey } from "./lib/entitlements/modules";
import { ConnectorUIBridge } from "./lib/connector-ui-bridge";
import { ThemeContext, applyTheme, readStoredTheme, type Theme } from "./lib/theme";
import { getSidebarNavGroups, hasMinRole, type SystemRole, type NavItem } from "./lib/rbac";
import { useResolvedEntitlements } from "@/hooks/use-entitlements";
import { BASE_PATH } from "./lib/utils";
import { HealthTabRedirect } from "./components/HealthTabRedirect";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AIUsageBadge } from "./components/topbar/AIUsageBadge";
import { CheckoutReturnNotice } from "./components/topbar/CheckoutReturnNotice";
import { BugReportButton } from "./components/bug-report/BugReportButton";
import { IntercomWidget } from "./components/IntercomWidget";
import { entitlementGateTestIds } from "./testids/authTestIds";
import { GlobalSearch } from "./components/GlobalSearch";
import { Sidebar } from "./components/Sidebar";
import { PageGuide } from "./components/PageGuideModal";
import { HelpMenu } from "./components/HelpMenu";
import { ConnectMcpModal } from "./components/ConnectMcpModal";
import { connectMcpTestIds } from "./testids/connectMcpTestIds";
import { CONNECT_MCP_MIN_ROLE } from "./lib/guides";
import { Icon } from "./pages/prototype-icons";
import { trackPageView } from "./lib/track";
import { useSetupJourney } from "./hooks/use-setup-journey";
import { shouldSendToSetup, setupJourneyEnabled, isSetupAside } from "@/lib/setup-gate";
import { useOnboardingStatus } from "./hooks/use-onboarding";
import { FirstIntegrationStatus } from "./components/FirstIntegration";
import { questionsGateEnabled, shouldAskQuestions } from "@/lib/questions-gate";
import { LoginPage } from "./pages/LoginPage";
import { CommandCenter } from "./pages/CommandCenter";
import { PendingApprovalPage } from "./pages/PendingApprovalPage";
import { AccessExpiredPage } from "./pages/AccessExpiredPage";
import { NoWorkspacePage } from "./pages/NoWorkspacePage";
import { topNavTestIds } from "./testids/topNavTestIds";

// Lazy load heavier pages
import { AppShellSkeleton } from "./components/AppShellSkeleton";

const AI_USAGE_ROLES: readonly string[] = ["platform_admin", "owner", "admin"];
const SuspenseFallback = () => <div className="fx-page"><div className="page-loading-skeleton" /></div>;

/* Not the landing route, so they do not belong in the initial bundle. Settings
   in particular pulls every tab it can route to -- billing, api keys, database,
   environments, oauth apps, secrets, static configs -- and paid for all of it
   on first paint of "/". LoginPage and PendingApprovalPage stay eager: AuthGate
   renders them directly, outside any Suspense boundary. */
const SettingsPage = React.lazy(() => import("./pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage").then(m => ({ default: m.ProfilePage })));
const IntegrationsPage = React.lazy(() => import("./pages/IntegrationsPage").then(m => ({ default: m.IntegrationsPage })).catch(() => ({ default: () => <div className="fx-page"><p>Loading…</p></div> })));
const ApiChangesPage = React.lazy(() => import("./pages/ApiChangesPage").catch(() => ({ default: () => <div className="fx-page"><p>Loading…</p></div> })));
const ConnectorDetailPage = React.lazy(() => import("./pages/ConnectorDetailPage").then(m => ({ default: m.ConnectorDetailPage })));
const ActivityPage = React.lazy(() => import("./pages/ActivityPage").then(m => ({ default: m.ActivityPage })).catch(() => ({ default: () => <div className="fx-page"><p>Loading…</p></div> })));
const HealthPage = React.lazy(() => import("./pages/HealthPage").then(m => ({ default: m.HealthPage })).catch(() => ({ default: () => <div className="fx-page"><p>Loading…</p></div> })));
const AccessPage = React.lazy(() => import("./pages/AccessPage").then(m => ({ default: m.AccessPage })));
const ConnectionsListPage = React.lazy(() => import("./pages/ConnectionsPage").then(m => ({ default: m.ConnectionsContent })).catch(() => ({ default: () => <div className="fx-page"><p>Loading…</p></div> })));
const CustomersPage = React.lazy(() => import("./pages/admin/TenantsPage").then(m => ({ default: m.WorkspacesPage })));
const YourProductPage = React.lazy(() => import("./pages/YourProductPage").then(m => ({ default: m.YourProductPage })));
const ConnectionDetailPage = React.lazy(() => import("./pages/ConnectionDetailPage").then(m => ({ default: m.ConnectionDetailPage })).catch(() => ({ default: () => <div className="fx-page"><p>Loading…</p></div> })));
const ConfigureInstallationPage = React.lazy(() => import("./pages/ConfigureInstallationPage").then(m => ({ default: m.ConfigureInstallationPage })));
const ConfigReviewPage = React.lazy(() => import("./pages/ConfigReviewPage"));
const TestCaseReviewPage = React.lazy(() => import("./pages/TestCaseReviewPage"));
const ConnectConnectorPage = React.lazy(() => import("./pages/ConnectConnectorPage"));
const SetupLinkPage = React.lazy(() => import("./pages/SetupLinkPage"));
const OrchestratorPage = React.lazy(() => import("./pages/OrchestratorPage"));
const GuidesPage = React.lazy(() => import("./pages/GuidesPage"));
const WidgetsPage = React.lazy(() => import("./pages/WidgetsPage").then(m => ({ default: m.WidgetsPage })).catch(() => ({ default: () => <div className="fx-page"><p>Loading…</p></div> })));
const ConfigEditorPage = React.lazy(() => import("./pages/ConfigEditorPage"));
const HubStandalonePage = React.lazy(() => import("./pages/HubStandalonePage").then(m => ({ default: m.HubStandalonePage })).catch(() => ({ default: () => <div className="fx-page"><p>Loading…</p></div> })));
const OnboardingPage = React.lazy(() => import("./pages/onboarding/OnboardingPage"));
const SignupQuestionsPage = React.lazy(() => import("./pages/onboarding/SignupQuestionsPage"));


/** Hides children if user does not have the required minimum role */
function RoleGate({ minRole, roles, children, fallback }: { minRole?: SystemRole; roles?: SystemRole[]; children: React.ReactNode; fallback?: React.ReactNode }): React.ReactElement | null {
  const { userContext, contextPending } = useAuth();
  // Never judge a role we are still resolving. A brand-new signup's token
  // carries no role claim, so userContext.role is a 'viewer' guess for the
  // first seconds of the session — denying on it sent first-run owners away
  // from /agent, /settings and /onboarding, the pages they had just signed up
  // to use. `contextPending` clears itself once the membership fetch answers,
  // and gives up rather than spinning forever.
  //
  // Decided here, above the early returns, rather than inside the deny branch:
  // the report below is an effect, and a hook cannot run conditionally.
  const denied = !contextPending
    && !!userContext
    && (roles
      ? !roles.includes(userContext.role as SystemRole)
      : minRole
        ? !hasMinRole(userContext.role, minRole)
        : false);

  // Flattened to strings so the effect's deps compare by value. `roles` is an
  // array literal in the JSX below, so it is a fresh reference every render and
  // would re-fire the effect on each one if used as a dependency directly.
  const requiredRole = roles ? roles.join(",") : (minRole ?? null);
  const actualRole = userContext?.role ?? null;

  // A visible control that then 403s is the failure mode UX rule 6 warns
  // about, and a frontend gate that disagrees with the backend is invisible
  // in every other signal — the user just bounces. This event is the only
  // thing that catches it in production, which is why its alert threshold is
  // zero and always was. The hook owns the once-per-denial guarantee; see the
  // note there for why an effect on its own was not enough.
  useDenialReport(denied, requiredRole, actualRole);

  if (contextPending) return fallback !== undefined ? <>{fallback}</> : <SuspenseFallback />;
  // No context at all: still never render nothing — a blank screen with no
  // explanation is how this page was reported in the first place.
  if (!userContext) return fallback !== undefined ? <>{fallback}</> : <Navigate to="/" replace />;
  if (denied) {
    if (fallback !== undefined) return <>{fallback}</>;
    // No fallback: every RoleGate is a full-page route guard, so redirect the
    // under-privileged user to the (ungated) home page instead of rendering a
    // blank screen. A viewer opening /agent used to get an empty page.
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * Hides children when the org is not entitled to a feature.
 *
 * Unlike RoleGate, this one has a loading state: entitlements arrive over the
 * network, and `useEntitlement` reports `false` until they do. Redirecting on
 * that initial `false` would bounce every entitled user off the page before
 * their own entitlements landed, so the redirect waits for a resolved answer.
 */
function EntitlementGate({ feature, children }: { feature: EntitlementKey; children: React.ReactNode }): React.ReactElement | null {
  const { enabled, isLoading, isError } = useEntitlement(feature);
  if (isLoading) return <SuspenseFallback />;
  // A failed read is not an answer. React Query reports `data: undefined` for a
  // 401 or a 500 exactly as it does for "false", so redirecting here turned any
  // blip on this one request into the page silently vanishing — the swallowed
  // auth failure this page was reported for. Say so instead.
  if (isError) return <EntitlementUnavailable />;
  if (!enabled) return <Navigate to="/integrations" replace />;
  return <>{children}</>;
}

/** Shown when we could not establish what the org is entitled to. */
function EntitlementUnavailable(): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center" data-testid={entitlementGateTestIds.unavailable}>
      <h1 className="text-lg font-semibold" data-testid={entitlementGateTestIds.unavailableHeading}>Couldn&rsquo;t load this page</h1>
      <p className="text-sm text-muted-foreground" data-testid={entitlementGateTestIds.unavailableMessage}>
        We couldn&rsquo;t check what your account has access to. Your session may have expired.
      </p>
      <button
        type="button"
        className="fx-btn fx-btn--secondary"
        onClick={() => window.location.reload()}
        data-testid={entitlementGateTestIds.unavailableRetryButton}
      >
        Retry
      </button>
    </div>
  );
}

/** /settings/customers/:id moved to /customers/:id; old links keep working. */
function CustomerRedirect(): React.ReactElement {
  const { customerId } = useParams();
  return <Navigate to={customerId ? `/customers/${encodeURIComponent(customerId)}` : "/customers"} replace />;
}

/**
 * The whole gate stack for the agent page, composed once.
 *
 * /agent and /agent/:threadId render the same OrchestratorPage, so they must
 * carry the same gates. They did not: /agent was entitlement-gated and the
 * thread deep link beside it was not, which left every thread URL — including
 * the ones AgentThreadRedirect produces — an ungated way into the page. Both
 * routes now render this, so a third agent route cannot be added with fewer
 * gates than the other two.
 */
function AgentRoute(): React.ReactElement {
  return (
    <ErrorBoundary>
      <RoleGate minRole="operator">
        <EntitlementGate feature="aiBuilder.integrations">
          <React.Suspense fallback={<SuspenseFallback />}>
            <OrchestratorPage />
          </React.Suspense>
        </EntitlementGate>
      </RoleGate>
    </ErrorBoundary>
  );
}

// Shown for unknown routes under the dashboard shell. Previously the catch-all
// silently redirected to "/", so a stale bookmark or a link to a deleted
// resource looked like a normal login redirect with no explanation.
function NotFoundPage(): React.ReactElement {
  return (
    <div style={{ padding: "64px 24px", textAlign: "center" }}>
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Page not found</h1>
      <p style={{ color: "var(--text-muted)", marginBottom: 20 }}>
        That page doesn’t exist or may have moved.
      </p>
      <Link to="/" className="fx-btn fx-btn--secondary">Back to home</Link>
    </div>
  );
}

// Legacy /agent/:threadId and /orchestrator-v2/:threadId deep links — <Navigate> does
// not interpolate params, so keep the thread and land on the canonical path.
function AgentThreadRedirect(): React.ReactElement {
  const { threadId } = useParams();
  return <Navigate to={`/agent/${threadId}`} replace />;
}

/**
 * Light/dark toggle.
 *
 * Sun/moon, matching the onboarding toggle. The pair was a light-bulb and a
 * closed eye, which is what the report objected to ("the switch mode icon is
 * not a good option -- maybe using the sun/moon"): a bulb reads as a hint or a
 * tip, and a closed eye as hide/preview, so neither says "theme". The onboarding
 * page was moved to sun/moon and this one -- the toggle on every OTHER page --
 * was left behind, so the same complaint survived the fix.
 *
 * Polarity is the destination, not the current state: in dark you see a sun
 * because clicking gives you light. That matches the aria-label below and the
 * onboarding toggle's own `theme === "light" ? Moon : Sun`.
 */

/** The public documentation site. External, so it never goes through appUrl(). */
const DOCS_URL = "https://docs.fastn.ai";

function ThemeToggle(): React.ReactElement {
  const { theme, toggle } = React.useContext(ThemeContext);
  return (
    <button
      type="button"
      className="fx-btn fx-btn--ghost fx-btn--icon fx-btn--sm"
      onClick={toggle}
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      data-testid={topNavTestIds.themeToggleButton}
    >
      <Icon name={theme === "dark" ? "sun" : "moon"} />
    </button>
  );
}

function TopBar(): React.ReactElement {
  const location = useLocation();
  const { userContext, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Which parent sections are expanded in the mobile drawer. Collapsed by
  // default so the drawer stays short; the section holding the current route
  // opens itself (see `open` below).
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [mcpOpen, setMcpOpen] = useState(false);

  const role = userContext?.role ?? 'end_user';
  // Same gate as the MCP page and the checklist's prompts, so no role gets a
  // trigger that dead-ends. Living in the top bar keeps it one click away from
  // every page, not only Home.
  const canConnectMcp = hasMinRole(role, CONNECT_MCP_MIN_ROLE);
  // Mirror the desktop rail (components/Sidebar.tsx): the same grouped nav with
  // its sub-pages, filtered by the same entitlement check. The old mobile menu
  // rendered getPrimaryNavItems — a flat top-level list — so a phone had no way
  // to reach Connectors, Connections, Workflows, Triggers, the Activity tabs or
  // any Settings page.
  const navGroups = getSidebarNavGroups(role);
  const { data: entitlements } = useResolvedEntitlements();
  const isEntitled = (item: { entitlement?: EntitlementKey }): boolean =>
    !item.entitlement || entitlements?.[item.entitlement] === true;

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <header className="topbar">
      {/* Brand and primary nav live in the left rail (components/Sidebar.tsx),
          which also owns the organization switcher and the account card. */}
      <div className="topbar-left" />
      {/* Column 2 of the topbar grid -- the design's centred search. */}
      <GlobalSearch />
      {/* The design's top bar carries the credits pill and nothing else. Theme
          and Report a bug sit beside it because the rail has no room the design
          did not already spend: its account card spans the full width. Both are
          ghost icon buttons so the pill stays the only filled thing here. */}
      <div className="topbar-right">
        <HelpMenu docsUrl={DOCS_URL} />
        <ThemeToggle />
        <BugReportButton />
        {/* Beside the credits pill: the partner's own AI tool is the other way
            to build, and the one that does not spend credits. Labeled, unlike
            the icon-only utilities, so it is findable without a color; the
            label drops below lg, where the bar has no room for it. "AI tool",
            not "agent": agent and assistant name built-in chats like fastn's
            builder. aria-label matches the visible text (WCAG 2.5.3). */}
        {canConnectMcp && (
          <button
            type="button"
            className="fx-btn fx-btn--secondary fx-btn--sm"
            onClick={() => setMcpOpen(true)}
            aria-label="Connect AI tools"
            title="Connect Claude, Cursor, ChatGPT or any MCP client to fastn"
            data-testid={connectMcpTestIds.topbarButton}
          >
            <Plug size={14} aria-hidden="true" />
            <span className="hidden lg:inline">Connect AI tools</span>
          </button>
        )}
        {/* Same roles as GET /api/v1/usage/ai (requireRole owner/admin, which
            also admits platform_admin). Mounted for every role, it polled a 403
            once a minute for everyone else and rendered nothing. */}
        {AI_USAGE_ROLES.includes(role) && <AIUsageBadge />}
        {AI_USAGE_ROLES.includes(role) && <CheckoutReturnNotice />}
      </div>
      {mcpOpen && <ConnectMcpModal open source="topbar" onClose={() => setMcpOpen(false)} />}
      {/* Mobile menu toggle */}
      <button
        className="topbar-mobile-toggle"
        data-testid={topNavTestIds.mobileToggleButton}
        onClick={() => setMobileMenuOpen((o) => !o)}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
      </button>
      {mobileMenuOpen && (
        <>
          {/* Click-away backdrop behind the right drawer. */}
          <div
            className="topbar-mobile-scrim"
            data-testid={topNavTestIds.mobileScrim}
            onClick={() => setMobileMenuOpen(false)}
          />
          <nav
            className="topbar-mobile-menu"
            data-testid={topNavTestIds.mobileMenu}
          >
            {navGroups.map((group, gi) => (
              <React.Fragment key={group.label ?? `group-${gi}`}>
                {group.label && <div className="topbar-mobile-group">{group.label}</div>}
                {group.items.filter(isEntitled).map((item) => {
                  const kids: NavItem[] = (item.children ?? []).filter(isEntitled);
                  if (kids.length === 0) {
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`topbar-mobile-link ${isActive(item.path) ? "topbar-mobile-link--active" : ""}`}
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid={topNavTestIds.mobileLink(item.label)}
                      >
                        <span>{item.label}</span>
                      </Link>
                    );
                  }
                  // A parent is an accordion toggle, not a link: tapping expands
                  // its pages instead of jumping away. The section holding the
                  // current route defaults open so the user sees where they are.
                  const open = openSections[item.path] ?? isActive(item.path);
                  return (
                    <React.Fragment key={item.path}>
                      <button
                        type="button"
                        className={`topbar-mobile-link topbar-mobile-parent ${open ? "topbar-mobile-parent--open" : ""}`}
                        aria-expanded={open}
                        onClick={() => setOpenSections((p) => ({ ...p, [item.path]: !open }))}
                        data-testid={topNavTestIds.mobileLink(item.label)}
                      >
                        <span>{item.label}</span>
                        <ChevronDown size={16} strokeWidth={1.75} aria-hidden />
                      </button>
                      {open &&
                        kids.map((kid) => (
                          <Link
                            key={kid.path}
                            to={kid.path}
                            className={`topbar-mobile-link topbar-mobile-sublink ${isActive(kid.path) ? "topbar-mobile-link--active" : ""}`}
                            onClick={() => setMobileMenuOpen(false)}
                            data-testid={topNavTestIds.mobileLink(kid.label)}
                          >
                            <span>{kid.label}</span>
                          </Link>
                        ))}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
            <div className="topbar-mobile-divider" />
            <Link
              to="/profile"
              className="topbar-mobile-link"
              onClick={() => setMobileMenuOpen(false)}
              data-testid={topNavTestIds.mobileProfileLink}
            >
              Profile
            </Link>
            <button
              className="topbar-mobile-link topbar-mobile-signout"
              data-testid={topNavTestIds.mobileSignOutButton}
              onClick={logout}
            >
              Sign out
            </button>
          </nav>
        </>
      )}
    </header>
  );
}

function ImpersonationBanner(): React.ReactElement | null {
  const { impersonating, stopImpersonation } = useAuth();
  if (!impersonating) return null;

  const remaining = Math.max(0, Math.ceil((impersonating.expiresAt - Date.now()) / 60_000));

  return (
    <div className="impersonation-banner">
      <span>
        Viewing as <strong>{impersonating.accountName}</strong>
        {remaining > 0 && <span style={{ marginLeft: 8, fontWeight: 400, fontSize: "12px" }}>Expires in {remaining} min</span>}
      </span>
      <button onClick={stopImpersonation}>Exit</button>
    </div>
  );
}


function PendingMembershipBanner(): React.ReactElement | null {
  const { pendingMemberships } = useAuth();
  if (pendingMemberships.length === 0) return null;

  const orgNames = pendingMemberships.map((m) => m.accountName).filter(Boolean);
  return (
    <div className="pending-banner">
      <span>
        Your request to join <strong>{orgNames.join(", ") || "an organization"}</strong> is pending approval.
      </span>
    </div>
  );
}

function DashboardLayout(): React.ReactElement {
  const location = useLocation();
  const { token } = useAuth();
  useEffect(() => { trackPageView(location.pathname, token); }, [location.pathname, token]);

  return (
    <ErrorBoundary>
      <div className="app-shell">
        <Sidebar />
        <div className="app-layout">
        <ImpersonationBanner />
        <PendingMembershipBanner />
        <TopBar />
        <main className="app-content">
          <FirstIntegrationStatus />
          <Routes>
            {/* Primary routes */}
            <Route path="/" element={<ErrorBoundary><CommandCenter /></ErrorBoundary>} />
            <Route path="/integrations" element={<ErrorBoundary><React.Suspense fallback={<SuspenseFallback />}><IntegrationsPage /></React.Suspense></ErrorBoundary>} />
            {/* ONE page for the whole story. The three routes below were three
                views of it — what changed, what we plan to do, how healthy we
                are — and the reader had to reassemble them by hand. They now
                redirect here so old links keep working; the detailed proposal
                view stays reachable for the deep dive. */}
            <Route path="/integrations/review-updates" element={<ErrorBoundary><React.Suspense fallback={<SuspenseFallback />}><ApiChangesPage /></React.Suspense></ErrorBoundary>} />
            <Route path="/integrations/health" element={<Navigate to="/integrations/review-updates" replace />} />
            <Route path="/integrations/changes" element={<Navigate to="/integrations/review-updates" replace />} />
            {/* Renamed from /integrations/api-changes. Kept for the same reason as
                the two above: this page absorbed Health and Upstream changes, so
                links to any of its former names still exist in the wild. */}
            <Route path="/integrations/api-changes" element={<Navigate to="/integrations/review-updates" replace />} />
            {/* Pending updates is the Updates tab on Health now. */}
            <Route path="/integrations/updates" element={<HealthTabRedirect tab="updates" />} />
            {/* Inside the shell, as in the design: the connector list is a third
                column between the app rail and the detail, not a replacement for
                the whole viewport. Registered standalone previously, so opening a
                connector removed the topbar and the app rail and left the small
                "<" beside "Connectors" as the only way back to Home/Settings. */}
            <Route path="/integrations/connectors/:slug" element={<ErrorBoundary><React.Suspense fallback={<SuspenseFallback />}><ConnectorDetailPage /></React.Suspense></ErrorBoundary>} />
            <Route path="/integrations/connectors/:slug/:actionSlug" element={<ErrorBoundary><React.Suspense fallback={<SuspenseFallback />}><ConnectorDetailPage /></React.Suspense></ErrorBoundary>} />
            <Route path="/integrations/connections/:id" element={<ErrorBoundary><React.Suspense fallback={<SuspenseFallback />}><ConnectionDetailPage /></React.Suspense></ErrorBoundary>} />
            <Route path="/widgets" element={<ErrorBoundary><RoleGate roles={["platform_admin", "owner", "admin", "developer", "operator", "viewer"]}><React.Suspense fallback={<SuspenseFallback />}><WidgetsPage /></React.Suspense></RoleGate></ErrorBoundary>} />
            <Route path="/widgets/preview" element={<Navigate to="/widgets/hub" replace />} />
            <Route path="/widgets/:widgetId" element={<ErrorBoundary><RoleGate roles={["platform_admin", "owner", "admin", "developer", "operator", "viewer"]}><React.Suspense fallback={<SuspenseFallback />}><WidgetsPage /></React.Suspense></RoleGate></ErrorBoundary>} />
            <Route path="/widgets/:widgetId/setups/:setupId/configure" element={<ErrorBoundary><RoleGate roles={["platform_admin", "owner", "admin", "developer", "operator", "viewer"]}><React.Suspense fallback={<SuspenseFallback />}><ConfigureInstallationPage /></React.Suspense></RoleGate></ErrorBoundary>} />
            {/* Legacy alias - old copied links keep working; renders the same setup config. */}
            <Route path="/widgets/:widgetId/installations/:installationId/configure" element={<ErrorBoundary><RoleGate roles={["platform_admin", "owner", "admin", "developer", "operator", "viewer"]}><React.Suspense fallback={<SuspenseFallback />}><ConfigureInstallationPage /></React.Suspense></RoleGate></ErrorBoundary>} />
            <Route path="/configs" element={<Navigate to="/settings/configs" replace />} />
            <Route path="/configs/new" element={<ErrorBoundary><RoleGate roles={["platform_admin", "owner", "admin", "developer"]}><React.Suspense fallback={<SuspenseFallback />}><ConfigEditorPage /></React.Suspense></RoleGate></ErrorBoundary>} />
            <Route path="/configs/:id/edit" element={<ErrorBoundary><RoleGate roles={["platform_admin", "owner", "admin", "developer"]}><React.Suspense fallback={<SuspenseFallback />}><ConfigEditorPage /></React.Suspense></RoleGate></ErrorBoundary>} />
            {/* Alerts lives under Health only. Alert emails, Slack messages and saved
                links still name /activity/alerts, so it opens the Health tab with its query. */}
            <Route path="/activity/alerts" element={<HealthTabRedirect tab="alerts" />} />
            <Route path="/activity/*" element={<ErrorBoundary><RoleGate roles={["platform_admin", "owner", "admin", "developer", "operator", "viewer"]}><React.Suspense fallback={<SuspenseFallback />}><ActivityPage /></React.Suspense></RoleGate></ErrorBoundary>} />
            {/* Health: Needs attention, then three existing pages as tabs: the
                cross-customer rollup (was /reporting, developer and up), Pending updates
                (was /integrations/updates, ungated) and Alerts. Each keeps the gate its
                page had, so no role gains or loses access. */}
            <Route path="/health" element={<ErrorBoundary><React.Suspense fallback={<SuspenseFallback />}><HealthPage /></React.Suspense></ErrorBoundary>} />
            <Route path="/reporting" element={<HealthTabRedirect tab="overview" />} />
            {/* Connections: a signed-in account is a runtime object, so it lives under Operate
                rather than as a tab among the things being built. */}
            <Route path="/connections" element={<ErrorBoundary><React.Suspense fallback={<SuspenseFallback />}><ConnectionsListPage /></React.Suspense></ErrorBoundary>} />
            {/* Customers moved out of /settings: it is where a partner watches each
                customer's installs and connections, not an account setting. Same
                gate the /settings/* route gave it. */}
            <Route path="/customers" element={<ErrorBoundary><RoleGate roles={["platform_admin", "owner", "admin", "developer", "operator"]}><React.Suspense fallback={<SuspenseFallback />}><CustomersPage /></React.Suspense></RoleGate></ErrorBoundary>} />
            <Route path="/customers/:customerId" element={<ErrorBoundary><RoleGate roles={["platform_admin", "owner", "admin", "developer", "operator"]}><React.Suspense fallback={<SuspenseFallback />}><CustomersPage /></React.Suspense></RoleGate></ErrorBoundary>} />
            <Route path="/settings/customers" element={<Navigate to="/customers" replace />} />
            <Route path="/settings/customers/:customerId" element={<CustomerRedirect />} />
            <Route path="/settings/workspaces" element={<Navigate to="/customers" replace />} />
            {/* API keys, the MCP server and Your API: one page under Embed. */}
            <Route path="/access" element={<ErrorBoundary><RoleGate roles={["platform_admin", "owner", "admin", "developer", "operator"]}><React.Suspense fallback={<SuspenseFallback />}><AccessPage /></React.Suspense></RoleGate></ErrorBoundary>} />
            <Route path="/settings/api-keys" element={<Navigate to="/access?tab=api-keys" replace />} />
            <Route path="/settings/mcp" element={<Navigate to="/access?tab=mcp" replace />} />
            <Route path="/settings/saas-connectors" element={<Navigate to="/your-product" replace />} />
            {/* Event webhooks moved to the product they call back. */}
            <Route path="/settings/event-webhooks" element={<Navigate to="/your-product?tab=webhooks" replace />} />
            {/* The partner's own product, registered as a connector. Same roles the /settings route gave it; the pane inside gates writes to admins. */}
            <Route path="/your-product" element={<ErrorBoundary><RoleGate roles={["platform_admin", "owner", "admin", "developer", "operator"]}><React.Suspense fallback={<SuspenseFallback />}><YourProductPage /></React.Suspense></RoleGate></ErrorBoundary>} />
            {/* Onboarding guides. Explicit roles (engineering rule 7), kept
                identical to the Guides nav entry in lib/rbac.ts so the route and
                the nav cannot drift into a page reachable by URL that the nav
                says the role cannot see. */}
            <Route path="/guides" element={<ErrorBoundary><RoleGate roles={["owner", "admin", "developer"]}><React.Suspense fallback={<SuspenseFallback />}><GuidesPage /></React.Suspense></RoleGate></ErrorBoundary>} />
            <Route path="/guides/:guideId" element={<ErrorBoundary><RoleGate roles={["owner", "admin", "developer"]}><React.Suspense fallback={<SuspenseFallback />}><GuidesPage /></React.Suspense></RoleGate></ErrorBoundary>} />

            {/* Settings with sidebar layout — SettingsPage handles internal routing + per-item role gates */}
            <Route path="/settings" element={<Navigate to="/settings/people" replace />} />
            <Route path="/settings/*" element={<ErrorBoundary><RoleGate minRole="operator"><React.Suspense fallback={<SuspenseFallback />}><SettingsPage /></React.Suspense></RoleGate></ErrorBoundary>} />

            {/* Other pages */}
            {/* /create-org is gone, not gated. A partner admin who used it was
                not creating an organization at all: POST /api/v1/orgs defaults
                `type` to end_org and `parentId` to the caller's own org for
                anyone below platform_admin, so the form minted a CUSTOMER with
                different copy, a different owner-invite path and a mandatory
                domain the customer flow does not ask for. Customers are created
                in Settings > Customers, which is the one place that says what it
                is doing. Kept as a redirect rather than a 404 because the menu
                item shipped and links to it exist. */}
            <Route path="/create-org" element={<Navigate to="/customers" replace />} />
            <Route path="/profile" element={<ErrorBoundary><React.Suspense fallback={<SuspenseFallback />}><ProfilePage /></React.Suspense></ErrorBoundary>} />

            {/* Redirects from old paths */}
            <Route path="/connectors" element={<Navigate to="/integrations?tab=connectors" replace />} />
            <Route path="/workflows" element={<Navigate to="/integrations?tab=workflows" replace />} />
            <Route path="/sync" element={<Navigate to="/integrations?tab=sync" replace />} />
            <Route path="/activity/triggers" element={<Navigate to="/integrations?tab=triggers" replace />} />
            <Route path="/settings/triggers" element={<Navigate to="/integrations?tab=triggers" replace />} />
            <Route path="/events" element={<Navigate to="/activity" replace />} />
            <Route path="/traces" element={<Navigate to="/activity?tab=traces" replace />} />
            <Route path="/agent" element={<AgentRoute />} />
            <Route path="/agent/:threadId" element={<AgentRoute />} />
            <Route path="/orchestrator" element={<Navigate to="/agent" replace />} />
            <Route path="/orchestrator/:threadId" element={<AgentThreadRedirect />} />
            <Route path="/orchestrator-v2" element={<Navigate to="/agent" replace />} />
            <Route path="/orchestrator-v2/:threadId" element={<AgentThreadRedirect />} />
            <Route path="/integrations/agent" element={<Navigate to="/agent" replace />} />
            <Route path="/integrations/agent-v2" element={<Navigate to="/agent" replace />} />
            {/* /connect, /config-review, /test-case-review are MCP hand-off links —
                registered as top-level standalone routes (no dashboard shell) in the
                outer <Routes> below, so they are intentionally NOT listed here. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          {/* Floats over the page rather than taking a column: a step points at
              one control, so the page has to stay fully visible under it. The
              two wrapper divs this replaced existed only to give the old side
              panel a column to stick in. */}
          <PageGuide />
        </main>
        </div>
      </div>
    </ErrorBoundary>
  );
}

/**
 * Sends a builder who has never run setup to the guided journey, once.
 *
 * The decision lives in lib/setup-gate.ts so it can be tested without a
 * renderer, and because getting it wrong is unrecoverable: /onboarding is
 * standalone with no DashboardLayout, so it has no sidebar and therefore no
 * account switcher, while this gate wraps every dashboard route. A wrong "yes"
 * leaves no page to navigate to and no control to switch org with.
 *
 * Deliberately narrow:
 *  - only owner/admin/developer — the roles that can actually pick connectors
 *    and create a widget. viewer/operator/end_user would hit a wizard they
 *    cannot complete, and `platform_admin` resolution is a cross-repo contract
 *    (engineering rule 19) that this must not touch.
 *  - never for a session acting for a workspace it switched into.
 *  - only while the query is settled and says "in_progress". A failed read
 *    renders the dashboard. So does a slow one, after a short hold: the first
 *    read of the session holds a loader for at most SETUP_HOLD_MAX_MS and only
 *    for its first attempt, so a slow or broken onboarding API can never hold
 *    the whole app hostage behind a spinner.
 *  - but never decided from a role that is still being resolved. That is a
 *    different wait from the one above and it is bounded: `contextPending`
 *    gives up after MEMBERSHIP_MAX_ATTEMPTS rather than spinning forever, so
 *    an org that never resolves still reaches the dashboard.
 *  - wrapped around DashboardLayout only, so the single-purpose standalone
 *    routes (/connect, /setup/:setupId, MCP hand-offs) are never hijacked.
 */
/** The longest SetupGate holds a loader before rendering the app anyway. */
const SETUP_HOLD_MAX_MS = 3000;
/** Set once any setup-journey read has answered in this page session. */
let setupJourneySettledThisSession = false;

function SetupGate({ children }: { children: React.ReactNode }): React.ReactElement {
  const { userContext, contextPending } = useAuth();
  const { search } = useLocation();
  const role = userContext?.role;
  const endOrgId = userContext?.endOrgId;
  const { data, isFetched, isPending, fetchStatus, failureCount } = useSetupJourney({ enabled: setupJourneyEnabled(role, endOrgId) });
  if (isFetched) setupJourneySettledThisSession = true;

  // Held until the journey read answers, as QuestionsGate holds for its own.
  // Rendering the app meanwhile is what flashed the dashboard between the
  // last signup question and onboarding: this gate only mounts once the
  // questions are done, so its read always starts from nothing right then.
  // Bounded three ways, so it never stands between a builder and the app:
  // only the first read of the session (an org switch clears the cache and
  // must not blank the layout it is switching inside), only its first
  // attempt (a failing read renders the app rather than riding out every
  // retry), and never past SETUP_HOLD_MAX_MS. Keyed on a read actually in
  // flight: a query that never runs (no org yet) is also "pending".
  const holding = !setupJourneySettledThisSession && isPending && fetchStatus === "fetching" && failureCount === 0;
  const [holdExpired, setHoldExpired] = useState(false);
  useEffect(() => {
    if (!holding) return;
    const t = setTimeout(() => setHoldExpired(true), SETUP_HOLD_MAX_MS);
    return () => clearTimeout(t);
  }, [holding]);

  // Same reason RoleGate holds: for the first seconds of a brand-new signup the
  // role is a 'viewer' guess, because the token is minted before provisioning
  // writes the claim. Judging that guess here is what made first-run users
  // report that there is no onboarding at all. `viewer` is not a builder, so
  // the journey query never even starts, nothing redirects, and the ungated
  // home page renders in its viewer shape - a greeting and two navigation
  // chips, no agent box, because every one of
  // those is gated on a role the session does not yet appear to have. That is
  // the screen that got filed, and it is the wizard's own entry point deciding
  // against itself on incomplete information.
  if (contextPending) return <SuspenseFallback />;

  if (holding && !holdExpired) return <SuspenseFallback />;

  if (shouldSendToSetup({
    role, endOrgId, isFetched,
    status: data?.status,
    journeyOrgId: data?.orgId ?? null,
    actingOrgId: userContext?.orgId,
  }) && !isSetupAside(search)) {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

/**
 * Asks a new signup the four signup questions before anything else, including
 * the setup journey. Rendered in place rather than redirected to, so there is
 * no route to loop through, and it wraps the same routes SetupGate does plus
 * /onboarding itself, so the questions always come first.
 *
 * Decided in lib/questions-gate.ts. Same posture as SetupGate: never judged on
 * a role still being resolved, and a loading or failed status read renders the
 * app rather than holding it. Existing users were marked skipped by
 * 1790592792488_skip-signup-questions-for-existing-users, so only new signups
 * are asked.
 */
function QuestionsGate({ children }: { children: React.ReactNode }): React.ReactElement {
  const { userContext, contextPending } = useAuth();
  // A platform_admin acting in a customer org holds their membership role
  // there, which questions-gate does not exempt; the person is still one.
  const role = userContext?.isPlatformAdmin ? "platform_admin" : userContext?.role;
  const endOrgId = userContext?.endOrgId;
  const enabled = !contextPending && questionsGateEnabled(role, endOrgId);
  const { data, isSuccess, isPending } = useOnboardingStatus({ enabled });

  // Held while the read is in flight, or the dashboard (or the setup wizard it
  // redirects to) flashes up before the questions replace it. Bounded: the
  // query does not retry, so it settles or fails and the app renders.
  if (contextPending || (enabled && isPending)) return <SuspenseFallback />;
  if (shouldAskQuestions({ role, endOrgId, isSuccess, status: data })) {
    return (
      <ErrorBoundary>
        <React.Suspense fallback={<SuspenseFallback />}>
          <SignupQuestionsPage saved={data?.savedState ?? {}} />
        </React.Suspense>
      </ErrorBoundary>
    );
  }
  return <>{children}</>;
}

function AuthGate({ children }: { children: React.ReactNode }): React.ReactElement {
  const { ready, authenticated, accessExpired, orgStatus } = useAuth();

  if (!ready) return <AppShellSkeleton />;

  if (!authenticated) {
    return <LoginPage />;
  }

  // Every membership is past its expiry — the API refuses this user on every
  // route, so there is no app to render. Checked before the pending branch:
  // the 403 leaves both membership arrays empty, so that branch cannot catch it.
  if (accessExpired) {
    return <AccessExpiredPage />;
  }

  // User has only pending memberships and no active ones — show waiting page
  if (orgStatus === "pending_approval") {
    return <PendingApprovalPage />;
  }

  // No org at all, or we could not find out. Nothing downstream is scoped
  // without one, so rendering the shell here only produces a dashboard whose
  // every request 401s — which is how a fresh account's connectors page came
  // to show "Session expired. Please refresh the page." over a healthy session.
  // Terminal and actionable instead of quietly broken.
  if (orgStatus === "none" || orgStatus === "error") {
    return <NoWorkspacePage />;
  }

  return <>
    <IntercomWidget />
    {children}
  </>;
}

/**
 * OAuth popup return page. This must be a real component doing its work in an
 * effect: the previous inline <script dangerouslySetInnerHTML> never executed,
 * because React 18 creates script elements via a detached innerHTML parse,
 * which marks them parser-inserted and therefore inert. The popup sat on
 * "Completing connection..." forever unless an unrelated ?oauth_success guard
 * happened to close it first — which UCL-authored callbacks (e.g. HubSpot)
 * never append.
 */
function OAuthDonePage(): React.ReactElement {
  const [stuckOpen, setStuckOpen] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  // This effect consumes the breadcrumb, so it must run exactly once. StrictMode
  // invokes effects twice in dev: the first run removed it and called replace(),
  // the second then found it missing, concluded it was a popup, and took the
  // close branch — the opposite decision, from the same page load.
  const decided = useRef(false);

  useEffect(() => {
    if (decided.current) return undefined;
    decided.current = true;
    const oauthFailure = new URLSearchParams(window.location.search).get("oauth_error");
    let hasOpener = false;
    try { hasOpener = !!(window.opener && !window.opener.closed); } catch { hasOpener = !!window.opener; }
    if (hasOpener) {
      // Popup flow: notify the opener, then close. The opener also closes us
      // on receipt, so either side winning is fine.
      // Same contract as the bootstrap: `oauth_error` on this URL is how the callback reports an
      // early exit, and ConnectModal shows `e.data.error` instead of closing. Dropping it made a
      // failed connect look exactly like a successful one.
      try {
        (window.opener as Window).postMessage(
          { type: "fastn:oauth-done", ...(oauthFailure ? { error: oauthFailure } : {}) },
          "*",
        );
      } catch { /* opener gone */ }
      window.close();
      const timer = window.setTimeout(() => setStuckOpen(true), 500);
      return () => window.clearTimeout(timer);
    }
    // No opener. Two very different situations look identical from here, and
    // treating them the same is what put the whole dashboard inside a 400px
    // popup: "Good afternoon, Saman" rendered in the OAuth window, which then
    // never closed.
    //
    // The breadcrumb tells them apart. It is written ONLY on the same-tab
    // fallback path, in the tab that is about to navigate. A popup cannot have
    // it: sessionStorage is copied to the popup when it is opened, which is
    // before that write happens, and later writes in the opener do not
    // propagate. So:
    //
    //   breadcrumb present -> this really is the user's tab mid-redirect; go back
    //   breadcrumb absent   -> we are the popup and our opener was severed
    //                          (COOP, or a cross-origin hop that dropped it)
    const back = takeOAuthReturn();
    if (back) {
      window.location.replace(back);
      return undefined;
    }
    // A popup that lost its opener. Closing usually still works — the window
    // was script-opened — but it can be refused, so say so rather than loading
    // the app in a window the user has to hunt down and close.
    //
    // On a FAILURE there is nobody left to tell, so closing would discard the only account of
    // what went wrong. Keep the window and render the reason.
    if (oauthFailure) {
      setFailure(oauthFailure);
      return undefined;
    }
    window.close();
    const timer = window.setTimeout(() => setStuckOpen(true), 500);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "system-ui", fontSize: 14, color: "var(--fs-text-muted)" }}>
      {failure ?? (stuckOpen ? "Connected. You can close this window." : "Completing connection...")}
    </div>
  );
}

export function App(): React.ReactElement {
  const [theme, setTheme] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  return (
    <AuthProvider>
      <ConnectorUIBridge>
      <ThemeContext.Provider value={{ theme, toggle }}>
        <BrowserRouter basename={BASE_PATH || undefined}>
          <PostHogPageviews />
          <Routes>
            {/* OAuth callback for the connect popup — closes the window after OAuth completes */}
            <Route path="/oauth-done" element={<OAuthDonePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/widgets/hub" element={
              <AuthGate>
                <ErrorBoundary><React.Suspense fallback={<SuspenseFallback />}><HubStandalonePage /></React.Suspense></ErrorBoundary>
              </AuthGate>
            } />
            {/* MCP hand-off links (get_connect_url / propose_configuration /
                submit_test_cases). These carry a scoped 20-min link token and must
                render as a bare single-purpose page — NO dashboard shell, TopBar,
                nav. AuthGate alone establishes the link-token
                session; the full-screen wrapper keeps the card centered.
                `h-screen`, not `min-h-screen`: `body` is `overflow: hidden`
                (only `.app-content` scrolls), and an auto-height box never
                becomes a scroller — tall content was clipped, not scrollable. */}
            <Route path="/connect/:connectorId" element={
              <AuthGate>
                <div className="h-screen w-full overflow-y-auto bg-background">
                  <ErrorBoundary><React.Suspense fallback={<SuspenseFallback />}><ConnectConnectorPage /></React.Suspense></ErrorBoundary>
                </div>
              </AuthGate>
            } />
            <Route path="/config-review/:draftId" element={
              <AuthGate>
                <div className="h-screen w-full overflow-y-auto bg-background">
                  <ErrorBoundary><React.Suspense fallback={<SuspenseFallback />}><ConfigReviewPage /></React.Suspense></ErrorBoundary>
                </div>
              </AuthGate>
            } />
            <Route path="/test-case-review/:draftId" element={
              <AuthGate>
                <div className="h-screen w-full overflow-y-auto bg-background">
                  <ErrorBoundary><React.Suspense fallback={<SuspenseFallback />}><TestCaseReviewPage /></React.Suspense></ErrorBoundary>
                </div>
              </AuthGate>
            } />
            <Route path="/setup/:setupId" element={
              <AuthGate>
                <div className="h-screen w-full overflow-y-auto bg-background">
                  <ErrorBoundary><React.Suspense fallback={<SuspenseFallback />}><SetupLinkPage /></React.Suspense></ErrorBoundary>
                </div>
              </AuthGate>
            } />
            {/* Guided first-run setup. Standalone (no dashboard shell) — it
                carries its own step rail, same as the prototype. */}
            <Route path="/onboarding/*" element={
              <AuthGate>
                <QuestionsGate>
                  <RoleGate roles={["owner", "admin", "developer"]}>
                    <ErrorBoundary><React.Suspense fallback={<SuspenseFallback />}><OnboardingPage /></React.Suspense></ErrorBoundary>
                  </RoleGate>
                </QuestionsGate>
              </AuthGate>
            } />
            <Route path="*" element={
              <AuthGate>
                <QuestionsGate>
                  <SetupGate>
                    <DashboardLayout />
                  </SetupGate>
                </QuestionsGate>
              </AuthGate>
            } />
          </Routes>
        </BrowserRouter>
      </ThemeContext.Provider>
      </ConnectorUIBridge>
    </AuthProvider>
  );
}
